import React from 'react';
import { Button, Progress } from 'reactstrap';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash-es';
import {
    faSave,
    faFileInvoiceDollar,
    faCheckCircle,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, {
    ApiRoutes,
    AppNavPaths,
    ServiceResponseResult,
} from '../Common';
import {
    AppPageForm,
    FlexCenterRow,
    FormGroupColumn,
    GroupedRow,
    onFieldChange,
    onReactSelectChanged,
    ToastMessage,
    FormCheckbox,
    Input,
} from '../common/forms/FormElements';
import { handleFormSaveError } from '../common/forms/ValidationError';

class EquipmentTag {
    id = null;
    description = '';
    tag = '';
    isActive = true;
}

class EquipmentTagForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.formRef = React.createRef();

        this.state = {
            originalData: new EquipmentTag(),
            equipmentTag: new EquipmentTag(),
            loading: true,
            saving: false,
        };

        this.handlers.change = this.handlers.change.bind(this);
        this.handlers.select = this.handlers.select.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps &&
            this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    onClearErrors = () => this.setState({ errors: {} });

    onSubmit = async () => {
        this.onClearErrors();
        this.setSaving(true);

        const cloned = clone(this.state.equipmentTag);

        if (this.props.location.pathname == AppNavPaths.EquipmentTagNew) {
            await this.create(cloned);
        } else {
            await this.update(cloned);
        }
    };

    setSaving = (b) => this.setState({ saving: b });

    create = async (tag) => {
        tag.id = 0;

        const response = await httpClient
            .post(ApiRoutes.equipmentTags.create(), tag)
            .catch(this.handleSaveError);

        if (response) {
            if (response.result === ServiceResponseResult.Ok) {
                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Save Successful"
                        message="Saved."
                    />,
                );

                this.context.history.push(
                    `${AppNavPaths.EquipmentTag}/${response.data}`,
                );
            } else {
                toast.error(
                    <ToastMessage
                        icon={faExclamationTriangle}
                        header="There was a problem saving"
                        message={`${response.message}`}
                    />,
                );
            }
        }
        this.setSaving(false);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    handlers = {
        change: onFieldChange,
        select: onReactSelectChanged,
    };

    async populateState() {
        const { id } = this.props.match.params;
        const equipmentTag = id
            ? await httpClient.js(ApiRoutes.equipmentTags.getById(id))
            : new EquipmentTag();

        this.setState({
            originalData: { ...equipmentTag },
            equipmentTag: {
                ...equipmentTag,
            },
            loading: false,
            saving: false,
        });
    }

    onCheckedChanged = (e) => {
        const { equipmentTag } = this.state;

        equipmentTag.isActive = e.target.checked;
        this.setState({ equipmentTag });
    };

    update = async (tag) => {
        const response = await httpClient
            .put(ApiRoutes.equipmentTags.update(tag.id), tag)
            .catch(this.handleSaveError);

        if (response) {
            if (response.result === ServiceResponseResult.Ok) {
                this.setState({
                    saving: false,
                    formValidated: false,
                });

                this.populateState();

                toast.success(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Save Successful"
                        message="Equipment Tag saved."
                    />,
                );
            } else {
                toast.error(
                    <ToastMessage
                        icon={faExclamationTriangle}
                        header="There was a problem saving"
                        message={`${response.message}`}
                    />,
                );
            }
        }
        this.setSaving(false);
    };

    renderForm() {
        const {
            equipmentTag,
            originalData,
            saving,
            errors,
            loading,
            formValidated,
        } = this.state;

        if (!equipmentTag) {
            return '';
        }

        const newEqupmentTag = parseInt(equipmentTag.id ?? 0) <= 0;

        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, equipmentTag)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="equipmentTagForm"
                    formHeadingIcon={faFileInvoiceDollar}
                    formHeading={
                        newEqupmentTag
                            ? 'New Equipment Tag'
                            : 'Edit Equipment Tag'
                    }
                    formName="equipmentTagForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    loading={loading}
                    backUrl={AppNavPaths.EquipmentTags}
                    backLabel="Back to Equipment Tags"
                >
                    <GroupedRow>
                        <FormGroupColumn>
                            <Input
                                label="Equipment Tag Name"
                                id="tag"
                                name="equipmentTag.tag"
                                value={equipmentTag.tag ?? ''}
                                onChange={this.handlers.change}
                                placeholder="Enter Tag"
                                error="Tag is required"
                                required
                            />
                            <Input
                                label="Equipment Tag Description"
                                id="description"
                                name="equipmentTag.description"
                                value={equipmentTag.description ?? ''}
                                onChange={this.handlers.change}
                                placeholder="Enter Tag Description"
                                error="Description is required."
                                required
                            />
                            <FormCheckbox
                                className="mt-2 mb-2"
                                id="chkEquipmentTagIsActive"
                                checked={equipmentTag.isActive}
                                onChange={this.onCheckedChanged}
                                labelText="Active"
                            />

                        </FormGroupColumn>
                    </GroupedRow>

                    <FlexCenterRow className="mb-3">
                        <Button
                            size="sm"
                            type="submit"
                            color="primary"
                            name="countyGroupForm"
                        >
                            <FontAwesomeIcon
                                className="mr-1"
                                icon={faSave}
                            />
                            {newEqupmentTag ? 'Save New Equipment Tag' : 'Save'}
                        </Button>
                    </FlexCenterRow>
                </AppPageForm>
            </>
        );
    }

    render() {
        const { loading } = this.state;

        if (loading) {
            return <Progress />;
        }

        return this.renderForm();
    }
}
export default EquipmentTagForm;
